import React, { FC, useState } from "react";

import { Button, Icon, Avatar } from "../../common";
import { Invoice } from "../../../types/InvoiceTypes";
import { InvoiceDetailsDataModal } from "../../modals";
import { AccountType } from "../../../types/UserTypes";
import { getCountryName } from "../../../utils/address";

type Props = {
  side: "from" | "to";
  data: Invoice;
  editable: boolean;
};

export const SideForm: FC<Props> = ({ side, data: invoice, editable }) => {
  const data = side === "from" ? invoice.issuer_data : invoice.payer_data;
  const account_type = data?.account_type;

  const [showModal, setShowModal] = useState(false);

  const name =
    account_type === AccountType.CONTRACTOR && data?.company_name
      ? data?.company_name
      : data?.name || "Business";

  const country = getCountryName(data?.country || "");

  const showSoleProprietorshipText =
    account_type === AccountType.CONTRACTOR && data?.company_name;

  const location = [data?.postal_code, data?.city, country]
    .filter((item) => !!item && item.length > 0)
    .join(", ");

  return (
    <>
      <div className="flex flex-col basis-1/2 justify-between items-start p-6 pt-4 border border-slate-600 rounded-xl min-h-[216px]">
        <header className="w-full flex flex-row items-top justify-between mb-16 gap-x-4">
          <div className="flex flex-col">
            <div className="theme-caption mb-1 uppercase">{side}</div>

            <div className="theme-text-default text-base break-all">{name}</div>

            {showSoleProprietorshipText && (
              <div className="theme-subheading mb-1">
                Sole proprietorship of {data?.name}
              </div>
            )}
          </div>

          <Avatar
            type={side === "to" ? "business" : "user"}
            title={name}
            className="basis-auto shrink-0 grow-0"
          />
        </header>

        {data ? (
          <div className="flex flex-row items-end justify-between w-full">
            <div className="flex-1 flex flex-col theme-subheading">
              {data.address && (
                <span className="break-all">{data.address}</span>
              )}

              {location && location.length > 0 && (
                <span className="break-all">{location}</span>
              )}

              {data.reg_number && (
                <span className="break-all">Reg. № {data.reg_number}</span>
              )}

              {data.tax_id && (
                <span className="break-all">Tax ID {data.tax_id}</span>
              )}
            </div>

            {editable && (
              <Button
                variant={Button.variant.SECONDARY}
                onClick={() => setShowModal(true)}
                icon
              >
                <Icon icon="Edit" size={24} disableFill />
              </Button>
            )}
          </div>
        ) : (
          editable && (
            <Button
              variant={Button.variant.PRIMARY}
              onClick={() => setShowModal(true)}
            >
              <span className="flex flex-row gap-x-3">
                <Icon icon="Add" size={24} disableFill />
                &nbsp; Add {side === "to" ? "business" : "your"} details
              </span>
            </Button>
          )
        )}
      </div>

      {showModal && (
        <InvoiceDetailsDataModal
          side={side}
          invoice={invoice}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};
